import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { translations, CURRENCIES, SHIPPING_CONFIG, COUNTRY_MAP, LANGUAGES, DEFAULT_VAT_BY_CURRENCY, VAT_RATES } from '../i18n/translations';

const LocaleContext = createContext(null);

const LOCALE_SET_FLAG = 'feron_locale_autoset';
const LS_LANG = 'feron_lang';
const LS_CURRENCY = 'feron_currency';
const LS_CURRENCY_MANUAL = 'feron_currency_manual';
const GEO_ENDPOINTS = ['https://ipwho.is/', 'https://ipapi.co/json/', 'https://get.geojs.io/v1/ip/country.json'];
const GEO_TIMEOUT_MS = 2500;
const SUPPORTED_LANG_CODES = LANGUAGES.map((l) => l.code);

// --- localStorage helpers -----------------------------------------------------
const readLS = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch (err) {
    console.error(`[Locale] localStorage read failed for ${key}:`, err);
    return fallback;
  }
};
const writeLS = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.error(`[Locale] localStorage write failed for ${key}:`, err);
  }
};

// --- Geo detection helper -----------------------------------------------------
const fetchCountryCode = async () => {
  for (const url of GEO_ENDPOINTS) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), GEO_TIMEOUT_MS);
    try {
      const res = await fetch(url, { cache: 'no-store', signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) continue;
      const data = await res.json();
      // ipapi.co returns {error:true} when rate-limited; ipwho.is returns {success:false}
      if (data && (data.error === true || data.success === false)) continue;
      const code = String(data.country_code || data.country || '').toUpperCase().trim();
      if (/^[A-Z]{2}$/.test(code)) return code;
    } catch (err) {
      clearTimeout(timer);
      // Silent — best-effort; failure leaves the India-first default in place
    }
  }
  return null;
};

// --- Currency formatter -------------------------------------------------------
const buildFormatter = (currency) => (inrAmount) => {
  const cur = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
  const val = inrAmount * cur.rate;
  if (cur.code === 'EUR') return `€${val.toFixed(2)}`;
  return `₹${Math.round(val)}`;
};

// --- Translator ---------------------------------------------------------------
const buildTranslator = (language) => (key, vars = {}) => {
  let s = (translations[language] && translations[language][key]) || translations.en[key] || key;
  Object.entries(vars).forEach(([k, v]) => {
    s = s.replace(`{${k}}`, v);
  });
  return s;
};

export const LocaleProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const stored = readLS(LS_LANG, 'en');
    // Migrate: if a removed language is stored, fall back to English
    return SUPPORTED_LANG_CODES.includes(stored) ? stored : 'en';
  });
  const [currency, setCurrencyState] = useState(() => readLS(LS_CURRENCY, 'INR'));
  const [detectedCountry, setDetectedCountry] = useState(null);

  // Wrapped setter used by the UI currency switcher — records an explicit user
  // choice so auto-detection stops overriding it.
  const setCurrency = useCallback((cur) => {
    writeLS(LS_CURRENCY_MANUAL, '1');
    setCurrencyState(cur);
  }, []);

  useEffect(() => { writeLS(LS_LANG, language); }, [language]);
  useEffect(() => { writeLS(LS_CURRENCY, currency); }, [currency]);

  // India-first, self-healing geo auto-detection. Runs on every load UNLESS the
  // user manually picked a currency. This corrects a currency that a flaky geo
  // provider set wrong on a previous visit (the old one-shot flag could cache a
  // wrong EUR forever).
  useEffect(() => {
    const manual = readLS(LS_CURRENCY_MANUAL, null) === '1';
    if (manual) return;

    let cancelled = false;
    (async () => {
      const code = await fetchCountryCode();
      if (cancelled) return;
      if (code) {
        setDetectedCountry(code);
        const mapping = COUNTRY_MAP[code];
        if (mapping) {
          setLanguage(mapping.lang);
          setCurrencyState(mapping.currency);
        } else {
          // Detected but not a Euro-market country → home currency (India-first).
          setCurrencyState('INR');
        }
        writeLS(LOCALE_SET_FLAG, '1');
      }
      // If detection failed (code === null) we leave the current value untouched;
      // the next successful load will self-correct.
    })();

    return () => { cancelled = true; };
  }, []);

  const t = useCallback((key, vars = {}) => {
    let s = (translations[language] && translations[language][key]) || translations.en[key] || key;
    Object.entries(vars).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
    return s;
  }, [language]);

  const formatPrice = useCallback((inrAmount) => {
    const cur = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
    const val = inrAmount * cur.rate;
    if (cur.code === 'EUR') return `€${val.toFixed(2)}`;
    return `₹${Math.round(val)}`;
  }, [currency]);

  // Product-aware price: uses explicit priceEUR/mrpEUR when set for EUR customers,
  // otherwise converts from the INR base value.
  const productPrice = useCallback((product, kind = 'price') => {
    if (!product) return '';
    if (currency === 'EUR') {
      const eurField = kind === 'mrp' ? product.mrpEUR : product.priceEUR;
      if (eurField !== undefined && eurField !== null && eurField > 0) {
        return `€${Number(eurField).toFixed(2)}`;
      }
    }
    const inrVal = kind === 'mrp' ? product.mrp : product.price;
    return formatPrice(inrVal || 0);
  }, [currency, formatPrice]);

  // Product-aware discount % that respects locale-specific EUR pricing:
  //   • EUR customer + product has explicit mrpEUR & priceEUR → derive % from those
  //   • EUR customer + product has discountEUR stored → use it directly
  //   • otherwise → fall back to product.discount (INR-derived %)
  const productDiscount = useCallback((product) => {
    if (!product) return 0;
    if (currency === 'EUR') {
      const mrpE = Number(product.mrpEUR);
      const priceE = Number(product.priceEUR);
      if (mrpE > 0 && priceE >= 0 && priceE <= mrpE) {
        return Math.round(((mrpE - priceE) / mrpE) * 100);
      }
      if (product.discountEUR !== undefined && product.discountEUR !== null) {
        return Math.round(Number(product.discountEUR));
      }
    }
    return Number(product.discount) || 0;
  }, [currency]);

  const value = useMemo(() => {
    const shipping = SHIPPING_CONFIG[currency] || SHIPPING_CONFIG.INR;
    const vatEntry = detectedCountry && VAT_RATES[detectedCountry]
      ? VAT_RATES[detectedCountry]
      : { rate: DEFAULT_VAT_BY_CURRENCY[currency] ?? 0, name: currency === 'EUR' ? 'Europe' : (currency === 'INR' ? 'India' : '') };
    return {
      language,
      setLanguage,
      currency,
      setCurrency,
      t,
      formatPrice,
      productPrice,
      productDiscount,
      currencySymbol: currency === 'EUR' ? '€' : '₹',
      freeShipThresholdINR: shipping.freeShipThresholdINR,
      shippingFeeINR: shipping.shippingFeeINR,
      deliveryDays: shipping.deliveryDays,
      detectedCountry,
      setDetectedCountry,
      vatRate: vatEntry.rate,
      vatCountryName: vatEntry.name
    };
  }, [language, currency, t, formatPrice, productPrice, productDiscount, detectedCountry]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);
